import { MouseEvent, useRef, useState } from "react";
import { CanvasTemplateMinimal } from "shared/datamodel/schemas/canvas-template";
import { createMenuOption } from "frontend/context-menu/optionsContextMenu";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { getPathPrefix } from "frontend/utils/getPathPrefix";
import TemplateCardTitle from "./template-card-title";
import style from "./template-card.module.css";
import TemplateCardImage from "./template-card-image";

export enum TemplateCardStyle {
  small = "small",
  regular = "regular",
}

interface TemplateCardProps {
  template: CanvasTemplateMinimal;
  previewAvailable: boolean;
  onClick: (template: CanvasTemplateMinimal) => void;
  onPreview: (template: CanvasTemplateMinimal) => void;
  onEditTemplate: (template: CanvasTemplateMinimal) => void;
  onDeleteTemplate: (template: CanvasTemplateMinimal) => void;
}

export function TemplateCard({
  template,
  previewAvailable,
  onClick,
  onPreview,
  onEditTemplate,
  onDeleteTemplate,
}: TemplateCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hover = useHover(cardRef);

  const options = (template: CanvasTemplateMinimal) => {
    const options = [
      createMenuOption({
        title: "Edit template",
        iconPath: getPathPrefix("/images/pencil-icon-new.svg"),
        id: "edit-template",
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          setShowMenu(false);
          onEditTemplate(template);
        },
      }),
      createMenuOption({
        title: "Delete template",
        iconPath: getPathPrefix("/images/trash-icon.svg"),
        id: "delete-template",
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          setShowMenu(false);
          onDeleteTemplate(template);
        },
      }),
    ];
    return options;
  };

  function menuClicked(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    setShowMenu(!showMenu);
  }

  function backgroundClicked() {
    setShowMenu(false);
  }

  const shouldShowMenu = !!(template.isOwner && (hover || showMenu));

  return (
    <div
      className={style.gridContainer}
      data-testid={template.upgradeRequired ? `upgrade-required-${template.name}` : template.name}
      onClick={() => onClick(template)}
      ref={cardRef}
    >
      <TemplateCardImage
        thumbnailImageURL={template.thumbnailImageURL}
        templateName={template.name}
        previewAvailable={previewAvailable}
        onPreviewClick={() => onPreview(template)}
        shouldShowMenu={shouldShowMenu}
        onMenuClick={menuClicked}
        menuButtonRef={menuButtonRef}
        upgradeRequired={template.upgradeRequired}
      />
      <TemplateCardTitle
        templateName={template.name}
        mondaySolutionId={template.metadata?.mondaySolutionId}
        downloadsCount={template.downloadsCount}
        showMenu={showMenu}
        menuButtonRef={menuButtonRef}
        backgroundClicked={backgroundClicked}
        options={options(template)}
      />
    </div>
  );
}
